<template>
  <div class="flex-1 pb-6 px-6 pt-3">
    <!-- Header Section -->
    <div class="bg-background border-b border-border px-8 py-2 -mx-6 mb-6" style="margin-left: -1.5rem; margin-right: -1.5rem;">
      <div class="flex items-center justify-between">
  <div>
          <h1 class="text-xl font-bold text-foreground mb-0">Leads</h1>
          <p class="text-muted-foreground text-sm mt-1">Manage your prospects and leads</p>
        </div>
        <div class="flex items-center gap-3">
          <b-form-select
              v-model="analyticsQuery"
              class="h-9 rounded-md border border-input bg-background px-3 text-sm"
              :options="['Last 7 Days', 'Last 30 Days', 'Last 90 Days', 'Lifetime']"
              @change="getAnalytics"
          ></b-form-select>
          <a
            v-if="showLandingButton"
            :href="landingPageUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold border border-primary bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
          >
            Go to Landing Page
          </a>
          <button v-if="!isZenSocial" type="button" class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2" @click="showCreateLeadModal = true">
            Create Customer
            <b-spinner v-if="createIsBusy" class="align-middle" small></b-spinner>
          </button>
          <button v-if="!isZenSocial" type="button" class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2" @click="showCreateManualLeadModal = true">
            Create Customer Manually
            <b-spinner v-if="createIsBusy" class="align-middle" small></b-spinner>
          </button>
        </div>
      </div>
    </div>
    <hr class="mb-6">

    <div class="px-2">
      <!-- Analytics Cards -->
      <div v-if="user.has_re || user.has_social_review" class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <!-- Search & Opt-ins Card -->
        <div class="rounded-lg bg-card text-card-foreground shadow-sm border border-border">
          <div class="flex flex-col space-y-1.5 p-6 pb-3">
            <h3 class="tracking-tight text-base font-semibold text-foreground mb-0">Search & Opt-ins</h3>
          </div>
          <div v-if="!loadingAnalytics" class="p-6 pt-0 space-y-3">
            <div class="flex items-center justify-between">
              <span class="text-sm text-muted-foreground">Prospects</span>
              <span class="text-xl font-bold text-foreground">{{analytics.search_prospects}}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-muted-foreground">Leads</span>
              <span class="text-xl font-bold text-success">{{analytics.search_leads}}</span>
            </div>
          </div>
          <div v-else class="p-6 text-center">
            <b-spinner small></b-spinner>
          </div>
        </div>

        <!-- Total Traffic Card -->
        <div class="rounded-lg bg-card text-card-foreground shadow-sm border border-border">
          <div class="flex flex-col space-y-1.5 p-6 pb-3">
            <div class="flex items-center justify-between">
              <h3 class="tracking-tight text-base font-semibold text-foreground mb-0">Total Traffic</h3>
              <span v-if="!loadingAnalytics" class="text-2xl font-bold text-warning">{{analytics.total_traffic}}</span>
            </div>
          </div>
          <div v-if="!loadingAnalytics" class="p-6 pt-0 space-y-3">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-monitor h-4 w-4 text-muted-foreground">
                  <rect width="20" height="14" x="2" y="3" rx="2"></rect>
                  <line x1="8" x2="16" y1="21" y2="21"></line>
                  <line x1="12" x2="12" y1="17" y2="21"></line>
                </svg>
                <span class="text-sm text-muted-foreground">Desktop</span>
              </div>
              <span class="text-lg font-bold text-foreground">{{analytics.total_traffic_desktop}}</span>
            </div>
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-smartphone h-4 w-4 text-muted-foreground">
                  <rect width="14" height="20" x="5" y="2" rx="2" ry="2"></rect>
                  <path d="M12 18h.01"></path>
                </svg>
                <span class="text-sm text-muted-foreground">Mobile</span>
      </div>
              <span class="text-lg font-bold text-success">{{analytics.total_traffic_mobile}}</span>
            </div>
          </div>
          <div v-else class="p-6 text-center">
            <b-spinner small></b-spinner>
          </div>
        </div>

        <!-- Total Leads Card -->
        <div class="rounded-lg bg-card text-card-foreground shadow-sm border border-border">
          <div class="flex flex-col space-y-1.5 p-6 pb-3">
            <div class="flex items-center justify-between">
              <h3 class="tracking-tight text-base font-semibold text-foreground mb-0">Total Leads</h3>
              <span v-if="!loadingAnalytics" class="text-2xl font-bold text-warning">{{analytics.total_leads}}</span>
            </div>
          </div>
          <div v-if="!loadingAnalytics" class="p-6 pt-0 space-y-3">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-monitor h-4 w-4 text-muted-foreground">
                  <rect width="20" height="14" x="2" y="3" rx="2"></rect>
                  <line x1="8" x2="16" y1="21" y2="21"></line>
                  <line x1="12" x2="12" y1="17" y2="21"></line>
                </svg>
                <span class="text-sm text-muted-foreground">Desktop</span>
              </div>
              <span class="text-lg font-bold text-foreground">{{analytics.total_leads_desktop}}</span>
            </div>
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-smartphone h-4 w-4 text-muted-foreground">
                  <rect width="14" height="20" x="5" y="2" rx="2" ry="2"></rect>
                  <path d="M12 18h.01"></path>
                </svg>
                <span class="text-sm text-muted-foreground">Mobile</span>
      </div>
              <span class="text-lg font-bold text-foreground">{{analytics.total_leads_mobile}}</span>
            </div>
          </div>
          <div v-else class="p-6 text-center">
            <b-spinner small></b-spinner>
          </div>
        </div>

        <!-- Conversion Rate Card -->
        <div class="rounded-lg bg-card text-card-foreground shadow-sm border border-border">
          <div class="flex flex-col space-y-1.5 p-6 pb-3">
            <div class="flex items-center justify-between">
              <h3 class="tracking-tight text-base font-semibold text-foreground mb-0">Conversion Rate</h3>
              <span v-if="!loadingAnalytics" class="text-2xl font-bold text-warning">{{analytics.conversion_rate}}%</span>
            </div>
          </div>
          <div v-if="!loadingAnalytics" class="p-6 pt-0 space-y-3">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-monitor h-4 w-4 text-muted-foreground">
                  <rect width="20" height="14" x="2" y="3" rx="2"></rect>
                  <line x1="8" x2="16" y1="21" y2="21"></line>
                  <line x1="12" x2="12" y1="17" y2="21"></line>
                </svg>
                <span class="text-sm text-muted-foreground">Desktop</span>
              </div>
              <span class="text-lg font-bold text-foreground">{{analytics.conversion_rate_desktop}}%</span>
            </div>
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-smartphone h-4 w-4 text-muted-foreground">
                  <rect width="14" height="20" x="5" y="2" rx="2" ry="2"></rect>
                  <path d="M12 18h.01"></path>
                </svg>
                <span class="text-sm text-muted-foreground">Mobile</span>
      </div>
              <span class="text-lg font-bold text-foreground">{{analytics.conversion_rate_mobile}}%</span>
            </div>
          </div>
          <div v-else class="p-6 text-center">
            <b-spinner small></b-spinner>
          </div>
        </div>
      </div>

      <!-- Table Section -->
      <div class="bg-card rounded-2xl border border-border overflow-hidden">
        <!-- Table Controls -->
        <div class="p-4 border-b border-border">
          <div class="flex items-center justify-between flex-wrap gap-3">
            <div class="flex items-center space-x-3 flex-wrap">
              <div class="flex items-center space-x-2">
                <span class="text-sm text-muted-foreground">Show</span>
              <b-form-select
                  v-model="datatable.queries.per_page"
                    class="h-8 rounded-md border border-input bg-background px-2 text-sm"
                  :options="[10, 15, 25, 50, 100]"
                  @change="getBusinesses"
              ></b-form-select>
              </div>
              <div class="flex items-center space-x-2">
                <span class="text-sm text-muted-foreground">Status Filter:</span>
              <b-form-select
                  v-model="datatable.queries.prospect_status"
                    class="h-8 rounded-md border border-input bg-background px-2 text-sm status-filter"
                    :options="[{ value: 'all', text: 'All' }, { value: 'leads', text: 'Lead' }, { value: 'prospect', text: 'Prospect' }]"
                  @change="getBusinesses"
              ></b-form-select>
            </div>
              <div class="flex items-center space-x-2">
                <span class="text-sm text-muted-foreground">Search:</span>
                <b-form-input
                    v-model="datatable.queries.search"
                    type="search"
                    placeholder="Search..."
                    class="flex rounded-md border border-input bg-background px-3 py-2 text-sm w-48 h-8"
                    @keyup.enter="getBusinesses"
                ></b-form-input>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <vue-json-to-csv :json-data="businesses" :csv-title="csvTitle()">
                <button class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 rounded-md px-3 bg-foreground hover:bg-foreground/90 text-background font-semibold h-8">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-download mr-2 h-3 w-3">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" x2="12" y1="15" y2="3"></line>
                  </svg>
                  Export
              </button>
              </vue-json-to-csv>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="relative w-full overflow-auto">
            <template v-if="!loadingBusinesses">
            <table class="w-full caption-bottom text-sm modern-table">
              <thead class="[&_tr]:border-b">
                <tr class="border-b transition-colors data-[state=selected]:bg-muted hover:bg-muted/50 bg-muted/50">
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 w-20" @click="sortBy('id')">
                    #
                  </th>
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0" @click="sortBy('small_date')">
                      Date
                  </th>
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0" @click="sortBy('business_name')">
                      Business
                  </th>
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0" @click="sortBy('full_name')">
                      Full Name
                  </th>
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0" @click="sortBy('email')">
                      Email
                  </th>
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0" @click="sortBy('phone_number')">
                      Phone
                  </th>
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0">Status</th>
                  <th class="h-12 px-4 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 text-right">Actions</th>
                </tr>
              </thead>
              <tbody class="[&_tr:last-child]:border-0">
                <tr v-for="business in businesses" :key="business.id" class="border-b transition-colors data-[state=selected]:bg-muted hover:bg-muted/30">
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0 font-medium">{{business.id}}</td>
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0 text-muted-foreground">{{business.small_date}}</td>
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0 font-medium">{{business.business_name}}</td>
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0 text-muted-foreground">{{business.full_name}}</td>
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0 text-muted-foreground">{{business.email}}</td>
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0 text-muted-foreground">{{business.phone_number}}</td>
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0">
                    <div class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2" :class="business.lead_status === 'Lead' ? 'bg-success/10 text-success border-success/20' : 'bg-primary/10 text-primary border-primary/20'">
                      {{business.lead_status}}
                  </div>
                  </td>
                  <td class="p-4 align-middle [&:has([role=checkbox])]:pr-0 text-right">
                    <div class="flex items-center justify-end space-x-2">
                    <router-link
                        :to="{ name: 'agency.businesses.edit', params: { id: business.id } }"
                        class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 rounded-md h-8 px-2 text-muted-foreground hover:text-foreground hover:bg-muted"
                    >
                      Edit
                    </router-link>
                      <button
                        :disabled="loadingRemove === business.id"
                        class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 delete-button h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                        @click="remove(business)"
                    >
                        <b-spinner v-if="loadingRemove === business.id" small />
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trash2 h-4 w-4">
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          <line x1="10" x2="10" y1="11" y2="17"></line>
                          <line x1="14" x2="14" y1="11" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
                  </template>
          <div v-else class="p-6 text-center">
            <b-spinner></b-spinner>
          </div>
              </div>

        <!-- Pagination -->
        <div class="p-4 border-t border-border flex items-center justify-between">
          <div class="text-sm text-muted-foreground">
            Total {{ totalBusinesses }} leads
                  </div>
          <div class="flex items-center space-x-2">
                      <b-pagination
                        v-model="datatable.queries.page"
                        :total-rows="totalBusinesses"
                        :per-page="datatable.queries.per_page"
              class="mb-0 modern-pagination"
                        @change="onPageChanged"
                      ></b-pagination>
          </div>
        </div>
      </div>
    </div>

    <b-modal v-model="showDeleteModal" title="Delete Business">
      <p v-if="selectedBusiness"
        >Are you sure you want to delete business "{{
          selectedBusiness.name
        }}"?</p
      >
      <template v-slot:modal-footer>
        <b-button variant="light" @click="showDeleteModal = false"
          >Cancel</b-button
        >
        <b-button variant="danger" @click="handleRemove">Delete</b-button>
      </template>
    </b-modal>
    <b-modal v-model="showWelcomeModal" size="lg" title="Welcome">
      <div class="text-center">
        <h4 class="text-success">Thanks For Signing Up For ReplyEngine</h4>
        <p>
        </p>
      </div>
      <template v-slot:modal-footer>
        <b-button variant="success" @click="showWelcomeModal = false"
          >Ok</b-button
        >
      </template>
    </b-modal>
    <b-modal v-model="showCreateLeadModal" size="lg" :hide-footer="true" :hide-header="true" modal-class="widget-modal">
      <div class="text-center">
        <h4>Create Lead</h4>
        <div>
          <reply-engine-generator
              v-if="user && user.agency"
              ref="rlg"
              :search-text="widgetDefaultText"
              class="widget-creator"
              :agency-id="user.agency.id"
              :starting-step="1"
              header-text="Search For Your Business"
              background-color="#ffffff"
              main-text-color="#222222"
              primary-button-background-color="#34a853"
              primary-button-text-color="#ffffff"
              :google-api-key="user.agency.landing_page.admin_settings.google_api_key"
              place-holder="Search"
              :manually-created="true"
              button-text="Create Lead"
              :force-busy="createIsBusy"
              :is-reply-engine="isReplyEngine"
              :is-tap-engine="isTapEngine"
              :is-social-review="isSocialReviews"
              :is-social-fusion="isSocialFusion"
              :is-instagram-widget="isInstaStream"
              :is-review-surge="isReviewSurge"
              :is-zensocial-business="isZensocialBusiness"
              @prospectCreated="prospectCreated"
          ></reply-engine-generator>
          <div class="business-access text-left" style="margin-top: -50px">
            <div v-if="user.has_re" class="d-inline-block mr-3 mb-3">
              <small class="mb-1 font-weight-bold d-block text-right" style="padding-right: 25px">
                <span v-if="isReplyEngine">Active</span>
                <span v-else>Disabled</span>
              </small>
              <div class="d-inline-flex">
                <span class="mr-2 d-flex">AI Review Reply</span>
                <b-form-checkbox
                    v-model="isReplyEngine"
                    size="sm"
                    name="initial-button"
                    switch
                    inline
                >
                </b-form-checkbox>
              </div>
            </div>
            <div v-if="user.has_te" class="d-inline-block mr-3 mb-3">
              <small class="mb-1 font-weight-bold d-block text-right" style="padding-right: 25px">
                <span v-if="isTapEngine">Active</span>
                <span v-else>Disabled</span>
              </small>
              <div class="d-inline-flex">
                <span class="mr-2 d-flex">NFC Reviews</span>
                <b-form-checkbox
                    v-model="isTapEngine"
                    size="sm"
                    name="initial-button"
                    switch
                    inline
                >
                </b-form-checkbox>
              </div>
            </div>
            <div v-if="user.has_social_review" class="d-inline-block mr-3 mb-3">
              <small class="mb-1 font-weight-bold d-block text-right" style="padding-right: 25px">
                <span v-if="isSocialReviews">Active</span>
                <span v-else>Disabled</span>
              </small>
              <div class="d-inline-flex">
                <span class="mr-2 d-flex">Social Review Posting</span>
                <b-form-checkbox
                    v-model="isSocialReviews"
                    size="sm"
                    name="initial-button"
                    switch
                    inline
                >
                </b-form-checkbox>
              </div>
            </div>
            <div v-if="user.has_social_fusion" class="d-inline-block mr-3 mb-3">
              <small class="mb-1 font-weight-bold d-block text-right" style="padding-right: 25px">
                <span v-if="isSocialFusion">Active</span>
                <span v-else>Disabled</span>
              </small>
              <div class="d-inline-flex">
                <span class="mr-2 d-flex">AI Social Media Posts</span>
                <b-form-checkbox
                    v-model="isSocialFusion"
                    size="sm"
                    name="initial-button"
                    switch
                    inline
                >
                </b-form-checkbox>
              </div>
            </div>
            <div v-if="user.has_review_requester" class="d-inline-block mr-3 mb-3">
              <small class="mb-1 font-weight-bold d-block text-right" style="padding-right: 25px">
                <span v-if="isReviewSurge">Active</span>
                <span v-else>Disabled</span>
              </small>
              <div class="d-inline-flex">
                <span class="mr-2 d-flex">Email/SMS Review Requests</span>
                <b-form-checkbox
                    v-model="isReviewSurge"
                    size="sm"
                    name="initial-button"
                    switch
                    inline
                >
                </b-form-checkbox>
              </div>
            </div>
            <div v-if="user.has_insta_stream" class="d-inline-block mr-3 mb-3">
              <small class="mb-1 font-weight-bold d-block text-right" style="padding-right: 25px">
                <span v-if="isInstaStream">Active</span>
                <span v-else>Disabled</span>
              </small>
              <div class="d-inline-flex">
                <span class="mr-2 d-flex">Instagram Feed</span>
                <b-form-checkbox
                    v-model="isInstaStream"
                    size="sm"
                    name="initial-button"
                    switch
                    inline
                >
                </b-form-checkbox>
              </div>
            </div>
            <div v-if="isRepuAgency" class="d-inline-block mr-3 mb-3">
              <small class="mb-1 font-weight-bold d-block text-right" style="padding-right: 25px">
                <span v-if="isZensocialBusiness">Enabled</span>
                <span v-else>Disabled</span>
              </small>
              <div class="d-inline-flex">
                <span class="mr-2 d-flex">Review Widgets</span>
                <b-form-checkbox
                    v-model="isZensocialBusiness"
                    size="sm"
                    name="initial-button"
                    switch
                    inline
                >
                </b-form-checkbox>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
    <b-modal v-model="showCreateManualLeadModal" size="lg" :hide-footer="true" title="Create Customer" modal-class="widget-modal">
      <CustomerCreateForm @close="showCreateManualLeadModal = false" />
    </b-modal>
  </div>
</template>

<script>
import VueJsonToCsv from 'vue-json-to-csv'
import ReplyEngineGenerator from '../../../../embed-form/src/components/Form'
import CustomerCreateForm from '@components/Common/CreateCustomer'
export default {
  components:{
    VueJsonToCsv,
    CustomerCreateForm,
    'reply-engine-generator': ReplyEngineGenerator
  },
  data() {
    return {
      analyticsQuery: 'Last 7 Days',
      json_export_labels:{

      },
      loadingAnalytics:false,
      analytics:{
        conversion_rate: '',
        conversion_rate_desktop: '',
        conversion_rate_mobile: '',
        search_leads: '',
        search_prospects: '',
        total_leads: '',
        total_leads_desktop: '',
        total_leads_mobile: '',
        total_traffic: '',
        total_traffic_desktop: '',
        total_traffic_mobile: '',
      },
      datatable: {
        items: [],
        totalItems: 1,
        columns: [
          { key: 'id', label: '#', sortable: true },
          { key: 'small_date', label: 'Date', sortable: true },
          { key: 'business_name', label: 'Business', sortable: true },
          { key: 'full_name', sortable: true },
          { key: 'email', label: 'Email', sortable: true },
          { key: 'phone_number', label: 'Phone', sortable: true },
          { key: 'lead_status', label: 'Status'},
          { key: 'actions', label: 'Actions', class:'actions' },
        ],
        queries: {
          per_page: 10,
          page: 1,
          status: 'active',
          prospect_status:'all',
          search: null,
          sort_by: 'id',
          sort_desc: true,
        },
      },
      showDeleteModal: false,
      selectedBusiness: null,
      loadingBusinesses: false,
      loadingImpersonate: false,
      loadingRemove: null,
      showWelcomeModal: false,
      createIsBusy:false,
      widgetDefaultText:'',
      showCreateLeadModal:false,
      showCreateManualLeadModal:false,
      isReplyEngine:false,
      isTapEngine:false,
      isSocialReviews:false,
      isSocialFusion:false,
      isReviewSurge:false,
      isInstaStream: false,
      isZensocialBusiness:false
    }
  },

  computed: {
    businesses() {
      return this.$store.getters['business/all'] || []
    },

    totalBusinesses() {
      return this.$store.getters['business/total']
    },

    reload() {
      return this.$store.getters['business/reload']
    },

    user() {
      return this.$store.getters['auth/user']
    },

    config() {
      return this.$store.getters['app/config']
    },

    showLandingButton() {
      if (!this.user?.agency) return false
      if (this.isZenSocial && this.user.agency.zensocial_landing_page?.zensocial_identifier) return true
      return !!(this.user.has_re || this.user.agency.free_fee || this.isRepuAgency)
    },

    landingPageUrl() {
      if (!this.user?.agency) return '#'
      if (this.isZenSocial && this.user.agency.zensocial_landing_page?.zensocial_identifier) {
        return `${window.location.origin}/p/${this.user.agency.zensocial_landing_page.zensocial_identifier}`
      }
      const protocol = window.location.protocol || 'https:'
      if (this.config?.custom_domain) {
        return `${protocol}//${this.config.custom_domain}/p/${this.user.agency.subdomain}`
      }
      const isStaging = typeof window !== 'undefined' && window.location.origin.includes('staging.')
      if (isStaging) {
        return `${window.location.origin}/p/${this.user.agency.subdomain}`
      }
      return `${protocol}//app.showmyrep.io/p/${this.user.agency.subdomain}`
    },
  },

  watch: {
    reload: (val) => {
      console.log(val)
    },
  },

  created() {
    this.$store.dispatch('business/setCurrentPage', 'index')
  },

  mounted() {
    this.getBusinesses();
    this.getAnalytics();
    console.log(this.user)
    if(this.user.has_te){
      this.isTapEngine = true;
    }
    if(this.user.has_re){
      this.isReplyEngine = true;
    }
    if(this.user.has_social_review){
      this.isSocialReviews = true;
    }
    if(this.user.has_social_fusion){
      this.isSocialFusion = true;
    }
    if(this.user.has_review_requester){
      this.isReviewSurge = true;
    }
    if(this.user.has_insta_stream){
      this.isInstaStream = true;
    }

    if(window.location.search.includes('create_lead')){
      this.showCreateLeadModal = true;
      let query = Object.assign({}, this.$route.query);
      delete query.create_lead;
      if(query.hasOwnProperty('business_name')) {
        this.widgetDefaultText = decodeURI(query.business_name);
        delete query.business_name;
      }
      this.$router.replace({ query });
    }

    if(window.location.search.includes('create_customer')){
      this.showCreateManualLeadModal = true;
      let query = Object.assign({}, this.$route.query);
      delete query.create_customer;
      this.$router.replace({ query });
    }

  },

  methods: {
    sortBy(column) {
      if (this.datatable.queries.sort_by === column) {
        this.datatable.queries.sort_desc = !this.datatable.queries.sort_desc
      } else {
        this.datatable.queries.sort_by = column
        this.datatable.queries.sort_desc = true
      }
      this.datatable.queries.page = 1
      this.getBusinesses()
    },
    getAnalytics() {
      this.loadingAnalytics = true
      this.$store
          .dispatch('agency/getAnalytics', {
            query: this.analyticsQuery,
            agencyId: this.user.agency.id
          })
          .then((res) => {
            if(res.hasOwnProperty('search_prospects')) {
              this.analytics = res
            }
            this.loadingAnalytics = false
          })
          .catch(() => {
            this.loadingAnalytics = false
          })
    },
    prospectCreated(business){
      this.showCreateLeadModal = false;
      this.createIsBusy = false;
      this.$router.push({
        name: 'agency.businesses.edit',
        params: { id: business.id },
      })
    },
    csvTitle(){
      return 'reply-engine-export-'+Date.now()
    },
    sortChanged(args) {
      this.datatable.queries.sort_by = args.sortBy
      this.datatable.queries.sort_desc = args.sortDesc
      this.datatable.queries.page = 1
      this.getBusinesses()
    },

    onPageChanged(page) {
      this.datatable.queries.page = page
      this.getBusinesses()
    },

    getBusinesses() {
      this.loadingBusinesses = true

      this.$store
        .dispatch('business/getAll', {
          queries: this.datatable.queries,
          page: 'index',
        })
        .then((res) => {
          this.loadingBusinesses = false
        })
        .catch(() => {
          this.loadingBusinesses = false
        })
    },

    impersonate(userId) {
      this.loadingImpersonate = true

      this.$store
        .dispatch('auth/impersonate', userId)
        .then((res) => {
          this.$store
            .dispatch('auth/getProfile')
            .then((user) => {
              this.$router.push('/' + user.role.toLocaleLowerCase())
              this.loadingImpersonate = false
            })
            .catch(() => {
              this.loadingImpersonate = false
            })
        })
        .catch(() => {
          this.loadingImpersonate = false
        })
    },

    remove(business) {
      this.showDeleteModal = true
      this.selectedBusiness = business
    },

    handleRemove() {
      this.showDeleteModal = false
      this.loadingRemove = this.selectedBusiness.id
      this.$store
        .dispatch('business/archive', this.selectedBusiness.id)
        .then(() => {
          this.loadingRemove = null
          this.getBusinesses()
        })
        .catch(() => {
          this.loadingRemove = null
        })
    },
  },
}
</script><style lang="scss" scoped>
  .status-filter{
    text-transform: capitalize;
  }
  .business-access .table{
    td{
      padding-top: 2px;
      padding-bottom: 2px;
    }
  }
  
  // Modern table styles
  table {
    border-collapse: collapse;
    
    thead tr {
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      
      th {
        user-select: none;
        
        &.cursor-pointer:hover {
          background-color: rgba(0, 0, 0, 0.02);
        }
      }
    }
    
    tbody tr {
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      
      &:last-child {
        border-bottom: none;
      }
      
      &:hover {
        background-color: rgba(0, 0, 0, 0.03);
      }
    }
  }
  
  // Modern pagination styles
  ::v-deep .modern-pagination {
    .pagination {
      display: flex;
      gap: 0.5rem;
      margin: 0;
      
      .page-item {
        .page-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 2.25rem;
          height: 2.25rem;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          font-weight: 500;
          border-radius: 0.375rem;
          border: 1px solid #d1d5db;
          background-color: #ffffff;
          color: #111;
          transition: all 0.15s ease;
          
          &:hover {
            background-color: rgba(0, 0, 0, 0.05);
            color: #111;
          }
    }
        
        &.active .page-link {
          background-color: rgb(36, 99, 235);
          border-color: rgb(36, 99, 235);
          color: #ffffff;
          
          &:hover {
            background-color: rgba(36, 99, 235, 0.9);
          }
        }
        
        &.disabled .page-link {
          pointer-events: none;
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }
  }
  
  // Gap utilities for flex
  .gap-3 {
    gap: 0.75rem;
  }
  
  // Negative margins
  .-mx-6 {
    margin-left: -1.5rem;
    margin-right: -1.5rem;
  }
</style><style>
.pac-container.pac-logo {
  z-index: 10000000;
}
.custom-control-input[disabled] ~ .custom-control-label::before, .custom-control-input:disabled ~ .custom-control-label::before{
  background: #ccc;
}
.custom-switch .custom-control-label::after{
  background: #efefef;
}
</style>